<template>
	<modal
		button-style="none"
		modal-style="styled"
		:open="openModal"
		title="Offerte aanvragen">
		<p>
			Bij bestellingen vanaf {{ selectedFormat.maxAmount }} bieden wij een offerte op maat, afgestemd op uw
			specifieke wensen.
			Profiteer van schaalvoordelen en ontvang een scherpe prijs voor uw oplage.
			<br>
			Laat hieronder uw gewenste oplage en contactgegevens achter.
			Ons team neemt zo snel mogelijk contact met u op om de mogelijkheden te bespreken en een passende offerte te
			bieden.
		</p>
		<hr>
		<label
			class="input-label"
			for="amount">Oplage</label>
		<span class="required" aria-required="true">*</span>
		<input
			class="input-field input-field--border mb-s"
			type="number"
			id="amount"
			v-model="amount"
			name="amount"
			:min="selectedFormat.maxAmount"
			required>
		<label
			class="input-label"
			for="email">E-mailadres</label>
		<span class="required" aria-required="true">*</span>
		<input
			class="input-field input-field--border mb-s"
			type="email"
			id="email"
			@input="validAddress(email)"
			v-model="email"
			name="email"
			required>
		<span
			class="text-danger field-validation-error"
			v-if="email !== '' && !validEmail">
			<span>Er moet een geldig e-mailadres ingevuld worden.</span>
		</span>
		<label
			class="input-label"
			for="phoneNumber">Telefoonnummer</label>
		<span class="required" aria-required="true">*</span>
		<div class="grid grid--gutters grid--ms-full mb-s">
			<div class="cell cell--fit">
				<input
					class="input-field input-field--border"
					type="text"
					id="phoneNumber"
					@input="validatePhoneNumber(phoneNumber)"
					v-model="phoneNumber"
					name="phoneNumber"
					required>
				<span
					class="text-danger field-validation-error"
					v-if="phoneNumber !== '' && !validPhoneNumber">
					<span>Er moet een geldig telefoonnummer ingevuld worden.</span>
				</span>
			</div>
			<div class="cell cell--auto">
				<button
					class="button button--cta button--icon button--full-width button--full-height"
					aria-hidden="Offerte aanvragen"
					@click="sendProposition"
					:disabled="!validEmail || !validAmount || !validPhoneNumber">
					Aanvragen
					<span
						class="button-loader loading"
						v-if="loading"
						style="margin: 0;"/>
				</button>
			</div>
		</div>
		<template v-if="response">
			<template v-if="quotationResponse.status === 200 && quotationResponse.body.configurationId">
				<div class="alertbox alertbox--success alertbox mv-m">
					<svg class="alertbox__icon alertbox__icon--success">
						<use xlink:href="#alert--success"/>
					</svg>
					<p
						class="alertbox__text">Aanvraag voltooid! U ontvangt binnenkort een e-mail met uw offerte.</p>
				</div>
			</template>
			<template v-else>
				<div class="alertbox alertbox--error alertbox mv-m">
					<svg class="alertbox__icon alertbox__icon--error">
						<use xlink:href="#alert--error"/>
					</svg>
					<p class="alertbox__text">
						Er ging iets fout.. Als dit probleem zich blijft voordoen kunt u contact opnemen met onze
						<a
							href="/klantenservice"
							title="klantenservice">klantenservice</a>.
					</p>
				</div>
			</template>
		</template>
		<hr>
		<div class="grid grid--gutters grid--m-full">
			<div class="cell cell--auto">
				<h3 class="hide-ms title--dot mb-m">Productgegevens</h3>

				<table
					v-if="check.hasSelectedOptions"
					class="configurator-overview__table">
					<tr>
						<td class="configurator-overview__table-title">Product:</td>
						<td
							class="configurator-overview__table-text"
							v-text="selectedProduct.name"/>
					</tr>
					<tr>
						<td class="configurator-overview__table-title">Formaat:</td>
						<td class="configurator-overview__table-text">{{ selectedFormat.productSize.fullName }}</td>
					</tr>
					<tr
						v-for="(option, index) in visibleSelectedOptions"
						:key="index">
						<td
							class="configurator-overview__table-title">{{ productOptionGroup(option.parentGroupId).name + ":" }}</td>
						<td
							class="configurator-overview__table-text"
							v-text="option.name"/>
					</tr>
					<spine
						table
						v-if="configuratorData.hasMultiplePages"/>
				</table>
			</div>
			<div class="cell cell--fit">
				<div
					v-if="previewUrl"
					class="preview-image-wrapper">
					<img
						:src="imagePath(mainImage)"
						class="content-block__image quotation-modal__image"
						width="300">
				</div>
				<img
					v-else
					:src="imagePath(mainImage)"
					class="content-block__image quotation-modal__image"
					width="300">
			</div>
		</div>
	</modal>
</template>

<script>
import { EventBus } from "@/store/index.js";
import { Helper } from "@/helpers";
import modal from "@/components/element/modal";
import spine from "@/components/element/spine";

export default {
	name: "Quotation",
	components: { modal, spine },
	data () {
		return {
			openModal: false,
			email: "",
			validEmail: false,
			amount: null,
			loading: false,
			response: false,
			phoneNumber: "",
			validPhoneNumber: false,
			validateTimeOut: null
		};
	},
	computed: {
		selectedProduct () {
			return this.$store.getters.getSelectedProduct;
		},
		selectedFormat () {
			return this.$store.getters.getSelectedFormat;
		},
		selectedOptions () {
			return Object.values(this.$store.getters.getSelectedOptions);
		},
		visibleSelectedOptions () {
			const hiddenOptionGroupIds = this.$store.getters.getHiddenOptionGroupIds;
			return this.selectedOptions.filter(option => !hiddenOptionGroupIds.includes(option.parentGroupId));
		},
		selectedCustomSize () {
			return this.$store.getters.getCustomSize;
		},
		check () {
			return this.$store.getters.checkData;
		},
		formatDescription () {
			return this.$store.getters.getSelectedFormatDescription;
		},
		configuratorData () {
			return this.$store.getters.getConfiguratorData;
		},
		priceTable () {
			return this.$store.getters.getPriceTable.filter(row => row.amount > 0);
		},
		offsetPriceTable () {
			return this.$store.getters.getOffsetPriceTable;
		},
		supportsOffset () {
			return this.$store.getters.currentConfigSupportsOffset;
		},
		mainImage () {
			return this.configuratorData.hasMultipleProducts
				? this.selectedProduct.iconPath
				: this.selectedFormat.iconPath;
		},
		previewUrl () {
			return this.$store.getters.getPreviewUrl;
		},
		validAmount () {
			return this.amount !== null
				&& this.amount !== ""
				&& this.amount >= this.selectedFormat.maxAmount;
		},
		quotationResponse () {
			return this.$store.getters.getQuotationResponse;
		}
	},
	methods: {
		imagePath (iconPath) {
			if (!this.previewUrl && this.previewUrl === null) {
				return iconPath;
			} else {
				return this.previewUrl;
			}
		},
		validAddress (email) {
			const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
			this.validEmail = true;
			if (this.validateTimeOut) {
				clearTimeout(this.validateTimeOut);
			}
			this.validateTimeOut = setTimeout(() => {
				this.validEmail = re.test(email);
			}, 500);
		},
		validatePhoneNumber (phoneNumber) {
			const validPhoneNumberRegex = /^[\\+]?[(]?[0-9]{3}[)]?[-\s\\.]?[0-9]{3}[-\s\\.]?[0-9]{4,6}$/im;
			this.validPhoneNumber = true;
			if (this.validateTimeOut) {
				clearTimeout(this.validateTimeOut);
			}
			this.validateTimeOut = setTimeout(() => {
				this.validPhoneNumber = validPhoneNumberRegex.test(phoneNumber);
			}, 500);
		},
		productOptionGroup: function (groupId) {
			const productGroupInfo = this.selectedFormat.productOptionGroups.find(g => g.id === groupId);

			return productGroupInfo;
		},
		formatPrice (price) {
			if (price) {
				return Helper.toCurrencyString(price);
			}
		},
		async sendProposition () {
			let token = null;
			this.loading = true;

			try {
				const siteKey = window.getRecaptchaSiteKeyByLanguage(document.documentElement.lang);
				token = await window.grecaptcha.execute(siteKey, { action: "quotation_mail_form" });
			} catch (error) {}

			this.$store.dispatch("_sendQuotationMail", {
				email: this.email,
				phoneNumber: this.phoneNumber,
				amount: this.amount,
				recaptchaToken: token
			});
		}
	},
	mounted () {
		EventBus.$on("openQuoteModal", () => {
			this.openModal = false;
			setTimeout(
				function () {
					this.openModal = true;
				}.bind(this),
				100
			);
		});

		window._appendGoogleRecaptchaScript(window.getRecaptchaSiteKeyByLanguage(document.documentElement.lang));
	},
	watch: {
		quotationResponse: function (a, b) {
			this.response = true;
			this.loading = false;
		},
		amount: function () {
			setTimeout(() => {
				if (this.amount < this.selectedFormat.maxAmount) {
					this.amount = this.selectedFormat.maxAmount;
				}
			}, 1000);
		}
	}
};
</script>
<style>
	.quotation-modal__image {
		max-width: 200px;
		margin-top: 64px;
	}

	@media screen and (max-width: 1000px) {
		.quotation-modal__image {
			margin-top: 0;
		}
	}
</style>



// WEBPACK FOOTER //
// src/components/overview/quotation.vue